import { MutableRefObject, useCallback } from 'react'

import { toast } from '@inno-ui/toast'
import { useQueryClient } from '@tanstack/react-query'
import { useMoveItemsToFolderMutation } from 'modules/desktop/modules/files-integration/api/hooks/useMoveItemsToFolder'
import { watchMoveJob } from 'modules/desktop/modules/files-integration/api/socket/watchMoveJob'
import { useTranslation } from 'react-i18next'
import { DesktopGridItem } from 'shared/types/desktop-grid'

import { useSelectionStore } from '../../../stores'
import { pendingItemPositions, pendingMoveOutItems } from '../utils'

type UseMoveItemsIntoFolderOptions = {
  itemsRef: MutableRefObject<DesktopGridItem[]>
  commitManualItems: (
    value:
      | DesktopGridItem[]
      | ((prevItems: DesktopGridItem[]) => DesktopGridItem[])
  ) => void
}

/**
 * Moves the given desktop items into a desktop folder. Optimistically removes
 * them, waits for the async move job's socket completion before refetching, and
 * restores their original positions if the move fails.
 */
export const useMoveItemsIntoFolder = ({
  itemsRef,
  commitManualItems
}: UseMoveItemsIntoFolderOptions) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'desktop.move_to_folder'
  })
  const queryClient = useQueryClient()
  const moveItemsToFolderMutation = useMoveItemsToFolderMutation()
  const clearSelection = useSelectionStore(state => state.clear)

  return useCallback(
    async (folderItemId: string, draggedItemIds: string[]) => {
      const currentItems = itemsRef.current
      const folder = currentItems.find(i => i.id === folderItemId)
      // folders are always shortcuts. this also narrows `folder` to
      // ShortcutGridItem for the rest of the scope.
      if (!folder || folder.itemType !== 'shortcut') return

      // only shortcuts can move into a folder. useDragAndDrop leaves any
      // widgets in the selection in place as a normal repositioning drop.
      const draggedItems = currentItems
        .filter(i => draggedItemIds.includes(i.id))
        .filter(i => i.itemType === 'shortcut')
      if (draggedItems.length === 0) return
      const draggedFileIds = draggedItems.map(i => i.fileId)

      // Mark as in-flight so the sync ignores them while the async move job
      // is still pending (a refetch may briefly still list them).
      draggedFileIds.forEach(id => pendingMoveOutItems.add(id))

      // Optimistically remove; the files-list refetch on success is authoritative.
      const movedIds = new Set(draggedItems.map(i => i.id))
      commitManualItems(prev => prev.filter(i => !movedIds.has(i.id)))
      clearSelection()

      const folderName = folder.label
      const errorState: { title: string; subTitle?: string } = {
        title: t('failed', { folder: folderName })
      }
      const promise = (async () => {
        try {
          const { jobId } = await moveItemsToFolderMutation.mutateAsync({
            destinationFolderId: folder.fileId,
            sourceItemIds: draggedFileIds
          })
          // Wait for the job-completed socket event before refetching, else
          // the refetch races the move and re-adds the items as "missing".
          if (jobId) {
            try {
              await watchMoveJob(jobId)
            } catch {
              // Socket failed — invalidate anyway; pendingMoveOutItems guards
              // against a stale refetch re-adding them.
            }
          }
          draggedFileIds.forEach(id => pendingMoveOutItems.remove(id))
          await queryClient.invalidateQueries({ queryKey: ['files-list'] })
        } catch (error) {
          if (error instanceof Error && error.message) {
            errorState.subTitle = error.message
          }
          // Restore original positions and clear the in-flight set so failed
          // items return to their cells instead of collapsing to top-left.
          draggedItems.forEach(i => {
            pendingItemPositions.set(i.fileId, i.position)
            pendingMoveOutItems.remove(i.fileId)
          })
          void queryClient.invalidateQueries({ queryKey: ['files-list'] })
          throw error
        }
      })()
      toast.info({
        asyncHandler: promise,
        asyncStates: {
          loading: {
            title: t('moving', {
              count: draggedFileIds.length,
              folder: folderName
            })
          },
          success: {
            title: t('moved', {
              count: draggedFileIds.length,
              folder: folderName
            })
          },
          error: errorState
        }
      })
    },
    [
      clearSelection,
      commitManualItems,
      itemsRef,
      moveItemsToFolderMutation,
      queryClient,
      t
    ]
  )
}
